import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, CheckCircle, Clock, Calendar as CalendarIcon, Globe, Loader2 } from 'lucide-react';
import { Button } from './Button';
import { Practitioner } from '@/data/practitioners';

interface BookCoachingModalProps {
  isOpen: boolean;
  onClose: () => void;
  practitioner: Practitioner;
}

export function BookCoachingModal({ isOpen, onClose, practitioner }: BookCoachingModalProps) {
  const hasIndividual = practitioner.certifications.includes('1-on-1 Coaching (Personal Growth & Development)') || practitioner.certifications.includes('1-on-1 Coaching (Leadership)');
  const hasTeams = practitioner.certifications.includes('Team Coaching');
  
  const [coachingType, setCoachingType] = useState<'1-on-1' | 'team'>(
    hasIndividual ? '1-on-1' : 'team'
  );
  
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Live Availability Slot State
  const [slots, setSlots] = useState<Array<{ start: string; end: string }>>([]);
  const [practitionerTimezone, setPractitionerTimezone] = useState<string>('Africa/Johannesburg');
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [loadingSlots, setLoadingSlots] = useState<boolean>(false);
  
  const visitorTimezone = typeof Intl !== 'undefined' && Intl.DateTimeFormat
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : 'UTC';

  useEffect(() => {
    if (isOpen) {
      setCoachingType(hasIndividual ? '1-on-1' : 'team');
      setIsSubmitted(false);
      setSelectedSlot(null);

      // Fetch live availability from practitioner's connected calendars
      setLoadingSlots(true);
      fetch(`/api/practitioners/${practitioner.id}/availability`)
        .then(res => res.json())
        .then(data => {
          if (data.success && Array.isArray(data.slots)) {
            setSlots(data.slots);
            if (data.practitionerTimezone) {
              setPractitionerTimezone(data.practitionerTimezone);
            }
          }
        })
        .catch(err => console.warn('Could not load practitioner slots:', err))
        .finally(() => setLoadingSlots(false));
    }
  }, [isOpen, hasIndividual, practitioner.id]);

  // Group slots by local date for visitor
  const slotsByDate = slots.reduce<Record<string, Array<{ start: string; end: string }>>>((acc, slot) => {
    try {
      const dateKey = new Date(slot.start).toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        timeZone: visitorTimezone
      });
      if (!acc[dateKey]) acc[dateKey] = [];
      acc[dateKey].push(slot);
    } catch {}
    return acc;
  }, {});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    // Get form data
    const formData = new FormData(e.target as HTMLFormElement);
    const firstName = formData.get('firstName') as string;
    const lastName = formData.get('lastName') as string;
    const email = formData.get('email') as string;
    const goals = formData.get('goals') as string;
    
    // Calculate price from canonical approvedRates (ZAR)
    const approvedRates = practitioner.approvedRates || {};
    let zarPriceNum = 2500;
    if (coachingType === '1-on-1') {
      zarPriceNum = approvedRates.oneOnOneRate || 2500;
    } else {
      zarPriceNum = approvedRates.teamRate || 15000;
    }
    const zarPrice = zarPriceNum.toFixed(2);

    try {
      const splitPercentage = 80; // Coach receives 80% (20% admin fee)
      const commissionAmount = Math.round(zarPriceNum * 0.20);
      
      const customData = JSON.stringify({
        practitionerId: practitioner.id,
        practitionerName: practitioner.name,
        certificationLevel: practitioner.certificationLevel,
        sessionType: coachingType === '1-on-1' ? '1-on-1 Coaching' : 'Team Coaching',
        splitPercentage,
        commissionAmount,
        goals,
        zarPrice: zarPriceNum,
        sessionDate: selectedSlot || null
      });

      const res = await fetch('/api/payfast/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          m_payment_id: `booking-${practitioner.id}-${Date.now()}`,
          amount: zarPrice,
          item_name: `${coachingType === '1-on-1' ? '1-on-1' : 'Team'} Coaching with ${practitioner.name}`,
          item_description: `Customer: ${firstName} ${lastName}`,
          name_first: firstName,
          name_last: lastName,
          email_address: email,
          custom_str1: customData,
          isSandbox: true
        })
      });
      const data = await res.json();
      if (data.payfastUrl && data.fields) {
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = data.payfastUrl;
        
        Object.keys(data.fields).forEach(key => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = data.fields[key];
          form.appendChild(input);
        });
        
        document.body.appendChild(form);
        form.submit();
      }
    } catch (err) {
      console.error(err);
      setIsLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-ink/60 backdrop-blur-sm z-50"
            aria-hidden="true"
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg bg-white shadow-2xl z-50 max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 md:p-8">
              <button 
                onClick={onClose}
                className="absolute top-6 right-6 text-ink/50 hover:text-rule-3 transition-colors"
                aria-label="Close modal"
              >
                <X className="w-6 h-6" />
              </button>

              {isSubmitted ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 text-rule-3 mx-auto mb-4" />
                  <h2 id="modal-title" className="text-3xl font-serif text-ink mb-4">Request Sent!</h2>
                  <p className="text-ink/70">
                    Thank you. The FIVE LENS team will contact you shortly to coordinate your coaching with {practitioner.name}.
                  </p>
                </div>
              ) : (
                <>
                  <h2 id="modal-title" className="text-3xl font-serif text-ink mb-2">Book Coaching</h2>
                  <p className="text-ink/70 mb-6 text-sm">
                    Request a coaching engagement with <span className="font-bold text-ink">{practitioner.name}</span> through the FIVE LENS platform.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-widest text-ink mb-3">
                        Coaching Type
                      </label>
                      <div className="grid grid-cols-2 gap-4">
                        {hasIndividual && (
                          <label className={`border p-4 cursor-pointer transition-colors ${coachingType === '1-on-1' ? 'border-rule-3 bg-rule-3/10' : 'border-ground-deep hover:border-ink/30'} ${!hasTeams ? 'col-span-2' : ''}`}>
                            <input 
                              type="radio" 
                              name="type" 
                              value="1-on-1" 
                              checked={coachingType === '1-on-1'}
                              onChange={() => setCoachingType('1-on-1')}
                              className="sr-only" 
                            />
                            <div className="font-bold text-ink mb-1">1-on-1 Coaching</div>
                            <div className="text-xs text-ink/60">Individual executive coaching</div>
                          </label>
                        )}
                        {hasTeams && (
                          <label className={`border p-4 cursor-pointer transition-colors ${coachingType === 'team' ? 'border-rule-3 bg-rule-3/10' : 'border-ground-deep hover:border-ink/30'} ${!hasIndividual ? 'col-span-2' : ''}`}>
                            <input 
                              type="radio" 
                              name="type" 
                              value="team" 
                              checked={coachingType === 'team'}
                              onChange={() => setCoachingType('team')}
                              className="sr-only" 
                            />
                            <div className="font-bold text-ink mb-1">Team Coaching</div>
                            <div className="text-xs text-ink/60">Group dynamics and alignment</div>
                          </label>
                        )}
                      </div>
                    </div>

                    {/* LIVE AVAILABILITY SLOT SELECTION */}
                    <div className="p-4 bg-ground-alt border border-ground-deep rounded-sm">
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-xs font-bold uppercase tracking-wider text-ink flex items-center gap-1.5">
                          <CalendarIcon className="w-3.5 h-3.5 text-rule-3" />
                          Select Session Time (Optional)
                        </label>
                        {loadingSlots && <Loader2 className="w-3.5 h-3.5 text-rule-3 animate-spin" />}
                      </div>

                      {loadingSlots ? (
                        <p className="text-xs text-ink/50 italic py-2">Checking coach live availability...</p>
                      ) : Object.keys(slotsByDate).length > 0 ? (
                        <div className="space-y-3 mt-2">
                          <div className="max-h-48 overflow-y-auto space-y-3 pr-1">
                            {Object.entries(slotsByDate).slice(0, 5).map(([dateLabel, daySlots]) => (
                              <div key={dateLabel}>
                                <span className="text-[11px] font-bold text-ink/70 block mb-1.5">{dateLabel}</span>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                                  {daySlots.slice(0, 6).map(slot => {
                                    const timeLabel = new Date(slot.start).toLocaleTimeString(undefined, {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      timeZone: visitorTimezone
                                    });
                                    const isSelected = selectedSlot === slot.start;
                                    return (
                                      <button
                                        type="button"
                                        key={slot.start}
                                        onClick={() => setSelectedSlot(isSelected ? null : slot.start)}
                                        className={`px-2.5 py-1.5 text-xs font-medium rounded-sm border transition-colors text-center ${
                                          isSelected
                                            ? 'bg-rule-3 text-white border-rule-3 font-bold'
                                            : 'bg-white border-ground-deep hover:border-rule-3/50 text-ink/80'
                                        }`}
                                      >
                                        {timeLabel}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                          
                          <div className="pt-2 border-t border-ground-deep/60 flex items-center gap-1.5 text-[10px] text-ink/60">
                            <Globe className="w-3 h-3 text-rule-3 shrink-0" />
                            <span>
                              Times shown in your local zone ({visitorTimezone}). Coach zone: {practitionerTimezone}.
                            </span>
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-ink/60">
                          Specific slots will be coordinated directly with your coach following booking confirmation.
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="firstName" className="block text-xs font-bold uppercase tracking-widest text-ink mb-2">First Name</label>
                        <input type="text" id="firstName" name="firstName" required className="w-full px-4 py-3 bg-ground-alt border border-ground-deep focus:border-rule-3 focus:ring-1 focus:ring-rule-3 outline-none" />
                      </div>
                      <div>
                        <label htmlFor="lastName" className="block text-xs font-bold uppercase tracking-widest text-ink mb-2">Last Name</label>
                        <input type="text" id="lastName" name="lastName" required className="w-full px-4 py-3 bg-ground-alt border border-ground-deep focus:border-rule-3 focus:ring-1 focus:ring-rule-3 outline-none" />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-xs font-bold uppercase tracking-widest text-ink mb-2">Work Email</label>
                      <input type="email" id="email" name="email" required className="w-full px-4 py-3 bg-ground-alt border border-ground-deep focus:border-rule-3 focus:ring-1 focus:ring-rule-3 outline-none" />
                    </div>

                    <div>
                      <label htmlFor="goals" className="block text-xs font-bold uppercase tracking-widest text-ink mb-2">Briefly describe your goals</label>
                      <textarea id="goals" name="goals" rows={3} required className="w-full px-4 py-3 bg-ground-alt border border-ground-deep focus:border-rule-3 focus:ring-1 focus:ring-rule-3 outline-none resize-none"></textarea>
                    </div>

                    <Button type="submit" className="w-full" disabled={isLoading}>
                      {isLoading ? 'Processing...' : 'Pay & Book Coaching'}
                    </Button>
                    <p className="text-center text-xs text-ink/50 mt-4">
                      By booking through FIVE LENS, you ensure quality standards and streamlined billing.
                    </p>
                  </form>
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
